/**
 * Shared file-attachment validation + types for SubmitTicketView (initial attachments) and
 * TicketDetailView (reply attachments). Centralizing the MIME allow-list + blocked extensions
 * means a future admin-driven policy ships in one file, not per-view.
 */

export const ATTACHMENT_ALLOWED_MIME_TYPES = new Set<string>([
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
  'text/plain', 'text/csv',
  'application/zip', 'application/x-zip-compressed',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
])

export const ATTACHMENT_BLOCKED_EXTENSIONS = new Set<string>([
  '.exe', '.bat', '.cmd', '.sh', '.ps1', '.msi', '.dll', '.scr',
])

export const ATTACHMENT_MAX_SIZE_MB = 10

export interface QueuedFile {
  file: File
  id: string
  /** If set, the file failed client-side validation — keep it in the queue so the user can
   *  see it and remove it explicitly; skipped during upload. */
  error?: string
}

export interface AttachmentValidationResult {
  /** Human-readable error for display. `null` means the file is valid. */
  error: string | null
}

/**
 * Validate a single file against the widget's allow-list + size cap. Callers pass a `t()`
 * localizer so error messages respect the widget's locale. Returns `{ error: null }` for
 * valid files or `{ error: '<localized string>' }` otherwise.
 */
export function validateAttachment(
  file: File,
  t: (key: string) => string,
  maxSizeMb: number = ATTACHMENT_MAX_SIZE_MB,
): AttachmentValidationResult {
  const ext = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '')
  if (ATTACHMENT_BLOCKED_EXTENSIONS.has(ext)) return { error: t('attach.invalidType') }

  // Accept OpenXML `.docx/.xlsx/.pptx` subtypes beyond the exact matches in the allow-list so
  // additions like `.docm` don't fail-closed unnecessarily. The blocked-extensions list is the
  // safety floor; the MIME allow-list is the ergonomic default.
  const officeOpenXml = file.type.startsWith('application/vnd.openxmlformats-officedocument.')
  if (!ATTACHMENT_ALLOWED_MIME_TYPES.has(file.type) && !officeOpenXml) {
    return { error: t('attach.invalidType') }
  }

  if (file.size > maxSizeMb * 1024 * 1024) return { error: t('attach.tooLarge') }
  return { error: null }
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

let queuedFileCounter = 0
/**
 * Stable client-side id for a queued file. `crypto.randomUUID` is available in modern browsers
 * but not in older WebViews; the counter fallback guarantees uniqueness within a page load.
 */
export function queuedFileId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `qf-${Date.now()}-${++queuedFileCounter}`
}
